import { createSignal, createResource, createMemo, createEffect, on, onCleanup } from "solid-js"
import { MOCK_SKILLS } from "./skill-market-mock"
import type {
  MarketplaceSkill,
  SkillCategory,
  UploadMetadata,
  CreateCategoryInput,
  UpdateSkillAttributes,
  PreparedSkillFile,
} from "./skill-market-types"
import type { ServerConnection } from "@/context/server"
import { authTokenFromCredentials } from "@/utils/server"

/**
 * Skill Marketplace Controller
 *
 * 直接调用 opencode 后端 API (/api/skill-market/*)，而非 zd-request.ts（后者指向 MEP 认证服务 /mep/api/*）。
 *
 * 关键点：
 * 1. 后端地址必须使用「当前选中服务器」的 URL + Basic Auth（由 useServer 提供），
 *    不能使用 location.origin 相对路径 —— nginx 只代理 /mep，不代理 /api，
 *    相对路径会导致 405/404。
 * 2. 必须携带 x-opencode-directory 头指向当前工作空间目录 —— serve 后端按请求解析
 *    location（?directory= / x-opencode-directory / process.cwd()），不带目录时
 *    install/uninstall/update 的文件操作和 SkillV2/CommandV2 reload 会落在错误的
 *    location（容器 cwd）上，导致「/」命令列表和 skill 内容不刷新。
 */

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ""
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

export function createSkillMarketController(
  getServer: () => ServerConnection.HttpBase | undefined = () => undefined,
  getDirectory: () => string | undefined = () => undefined,
) {
  const [selectedCategory, setSelectedCategory] = createSignal<string>("all")
  const [selectedSkillId, setSelectedSkillId] = createSignal<string | null>(null)
  const [selectedFilePath, setSelectedFilePath] = createSignal<string | null>(null)
  const [fileContent, setFileContent] = createSignal<{ path: string; content: string; is_markdown: boolean } | null>(null)
  const [fileLoading, setFileLoading] = createSignal(false)
  const [searchQuery, setSearchQuery] = createSignal("")
  const [installing, setInstalling] = createSignal(false)
  const [uploading, setUploading] = createSignal(false)
  const [showUpload, setShowUpload] = createSignal(false)
  const [error, setError] = createSignal<string | null>(null)

  async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const server = getServer()
    if (!server?.url) throw new Error("未选择服务器，请在设置中选择 opencode 后端地址")

    const base = `${server.url.replace(/\/+$/, "")}/api/skill-market`
    const headers: Record<string, string> = {
      ...((options?.headers as Record<string, string>) ?? {}),
    }
    if (server.password) {
      headers.Authorization = `Basic ${authTokenFromCredentials({
        username: server.username,
        password: server.password,
      })}`
    }
    // 指向当前工作空间，后端据此解析 location（文件安装目录 + reload 的服务实例）
    const directory = getDirectory()
    if (directory) {
      headers["x-opencode-directory"] = encodeURIComponent(directory)
    }

    const res = await fetch(`${base}${path}`, { ...options, headers })
    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${res.statusText}`)
    }
    if (res.status === 204) return undefined as T
    return res.json()
  }

  // 每页条数
  const PAGE_SIZE = 20

  // mock 兜底：当后端返回空（或请求失败）时，用 MOCK_SKILLS 按 category/search 过滤 + 分页切片
  // 作为影子分页源，保证空表时仍有数据可展示，且能验证滚动加载（50 条「其他」→ 3 页）
  function mockPage(cat: string, query: string, pageOffset: number): { slice: MarketplaceSkill[]; hasMore: boolean } {
    let pool = MOCK_SKILLS
    if (cat !== "all") pool = pool.filter((s) => s.category === cat)
    if (query) {
      const q = query.toLowerCase()
      pool = pool.filter(
        (s) =>
          (s.name ?? "").toLowerCase().includes(q) ||
          (s.display_name ?? "").toLowerCase().includes(q) ||
          (s.description ?? "").toLowerCase().includes(q),
      )
    }
    const slice = pool.slice(pageOffset, pageOffset + PAGE_SIZE)
    return { slice, hasMore: pageOffset + slice.length < pool.length }
  }

  // —— 分类：从后端 GET /api/skill-market/categories 获取 ——
  // 前端渲染只需要 code + label；计数由 categoryCounts 从已加载/mock 技能前端统计（恢复原逻辑）。
  const [categoriesResource, { refetch: refetchCategories }] = createResource(async (): Promise<SkillCategory[]> => {
    try {
      const data = await apiFetch<SkillCategory[]>("/categories")
      const list = data ?? []
      return [{ code: "all", label: "全部技能" }, ...list.map((c) => ({ code: c.code, label: c.label }))]
    } catch {
      return [{ code: "all", label: "全部技能" }]
    }
  })

  const categories = createMemo<SkillCategory[]>(() => categoriesResource() ?? [])

  // —— 技能列表：滚动分页（offset/limit），按 category/search 服务端过滤 ——
  const [loadedSkills, setLoadedSkills] = createSignal<MarketplaceSkill[]>([])
  const [offset, setOffset] = createSignal(0)
  const [hasMore, setHasMore] = createSignal(true)
  const [loadingMore, setLoadingMore] = createSignal(false)
  const [refreshing, setRefreshing] = createSignal(false)

  // mock 兜底标志：true 时 categoryCounts 按全量 MOCK_SKILLS 统计（避免分页导致计数跳变）
  const [mockFallback, setMockFallback] = createSignal(false)

  // 分类计数：mock 兜底时按全量 MOCK_SKILLS 统计（"其他"显示 50）；否则按已加载技能统计
  const categoryCounts = createMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {}
    const source = mockFallback() ? MOCK_SKILLS : loadedSkills()
    for (const skill of source) {
      const cat = skill.category ?? "other"
      counts[cat] = (counts[cat] ?? 0) + 1
    }
    return counts
  })

  // 搜索 debounce 计时
  let searchTimer: ReturnType<typeof setTimeout> | undefined

  async function loadPage(reset: boolean) {
    if (loadingMore() || refreshing()) return
    const cat = selectedCategory()
    const query = searchQuery()
    const pageOffset = reset ? 0 : offset()
    if (reset) {
      setRefreshing(true)
      setHasMore(true)
    } else {
      setLoadingMore(true)
    }
    try {
      const params = new URLSearchParams()
      if (cat !== "all") params.set("category", cat)
      if (query) params.set("search", query)
      params.set("limit", String(PAGE_SIZE))
      params.set("offset", String(pageOffset))
      const data = await apiFetch<MarketplaceSkill[]>(`?${params.toString()}`)
      const page = data ?? []
      if (page.length > 0) {
        // 后端有数据 → 正常分页
        setMockFallback(false)
        if (reset) {
          setLoadedSkills(page)
          setOffset(page.length)
        } else {
          setLoadedSkills((prev) => [...prev, ...page])
          setOffset(pageOffset + page.length)
        }
        setHasMore(page.length === PAGE_SIZE)
      } else {
        // 后端空 → mock 兜底分页（reset 与 loadMore 都走这条，保证滚动加载第 2/3 页）
        setMockFallback(true)
        const { slice, hasMore } = mockPage(cat, query, pageOffset)
        if (reset) {
          setLoadedSkills(slice)
          setOffset(slice.length)
        } else {
          setLoadedSkills((prev) => [...prev, ...slice])
          setOffset(pageOffset + slice.length)
        }
        setHasMore(hasMore)
      }
    } catch {
      // 请求失败 → 仅首页无已加载数据时用 mock 兜底第 1 页；非首页失败则停止加载
      if (reset && loadedSkills().length === 0) {
        setMockFallback(true)
        const { slice, hasMore } = mockPage(cat, query, 0)
        setLoadedSkills(slice)
        setOffset(slice.length)
        setHasMore(hasMore)
      } else {
        setHasMore(false)
      }
    } finally {
      setLoadingMore(false)
      setRefreshing(false)
    }
  }

  function loadMore() {
    if (!hasMore() || loadingMore() || refreshing()) return
    void loadPage(false)
  }

  // 切换分类 → 重置到第 1 页（defer：首次由下方手动 loadPage(true) 触发）
  createEffect(
    on(selectedCategory, () => {
      void loadPage(true)
    }, { defer: true }),
  )

  // 搜索 → debounce 300ms 后重置到第 1 页
  createEffect(
    on(searchQuery, () => {
      if (searchTimer !== undefined) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        void loadPage(true)
      }, 300)
    }, { defer: true }),
  )

  onCleanup(() => {
    if (searchTimer !== undefined) clearTimeout(searchTimer)
  })

  // 首次加载
  void loadPage(true)

  const skills = createMemo<MarketplaceSkill[]>(() => loadedSkills())

  const selectedSkill = createMemo<MarketplaceSkill | null>(() => {
    const id = selectedSkillId()
    if (!id) return null
    return skills().find((s) => s.id === id) ?? null
  })

  // 选中 skill 时自动选中 SKILL.md（如果有）
  function selectSkill(id: string) {
    // 点击已选中的 skill → 取消选择（关闭详情抽屉）
    if (selectedSkillId() === id) {
      deselectSkill()
      return
    }
    setSelectedSkillId(id)
    setFileContent(null)
    // 自动选中 SKILL.md
    const skill = skills().find((s) => s.id === id)
    if (skill?.file_manifest && skill.file_manifest.includes("SKILL.md")) {
      setSelectedFilePath("SKILL.md")
      fetchFile(id, "SKILL.md")
    } else {
      setSelectedFilePath(null)
    }
  }

  function deselectSkill() {
    setSelectedSkillId(null)
    setSelectedFilePath(null)
    setFileContent(null)
  }

  function selectFile(filePath: string) {
    const skillId = selectedSkillId()
    if (!skillId) return
    setSelectedFilePath(filePath)
    fetchFile(skillId, filePath)
  }

  async function fetchFile(skillId: string, filePath: string) {
    setFileLoading(true)
    setError(null)
    try {
      const encoded = encodeURIComponent(filePath)
      const data = await apiFetch<{ path: string; content: string; is_markdown: boolean }>(
        `/${skillId}/file?path=${encoded}`,
      )
      setFileContent(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : "读取文件失败")
      setFileContent(null)
    } finally {
      setFileLoading(false)
    }
  }

  // 本地 patch 已加载列表中的某个 skill（用于 install/uninstall/version 后即时更新，避免重置分页）
  function patchSkill(id: string, patch: Partial<MarketplaceSkill>) {
    setLoadedSkills((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))
  }

  async function installSkill(skill: MarketplaceSkill, scope: "global" | "project" = "global") {
    setInstalling(true)
    setError(null)
    try {
      const updated = await apiFetch<MarketplaceSkill>(`/${skill.id}/install`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope }),
      })
      // 本地 patch，保持滚动位置
      patchSkill(skill.id, updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : "安装失败")
    } finally {
      setInstalling(false)
    }
  }

  async function uninstallSkill(skill: MarketplaceSkill, scope: "global" | "project" = "global") {
    setInstalling(true)
    setError(null)
    try {
      await apiFetch(`/${skill.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope }),
      })
      // 本地 patch 卸载状态
      patchSkill(skill.id, {
        is_installed_global: scope === "global" ? false : skill.is_installed_global,
        is_installed_project: scope === "project" ? false : skill.is_installed_project,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : "卸载失败")
    } finally {
      setInstalling(false)
    }
  }

  async function removeSkill(skill: MarketplaceSkill) {
    setInstalling(true)
    setError(null)
    try {
      await apiFetch(`/${skill.id}/remove`, {
        method: "DELETE",
      })
      // 删除后清除选中状态 + 从已加载列表移除 + 刷新分类计数
      setSelectedSkillId(null)
      setLoadedSkills((prev) => prev.filter((s) => s.id !== skill.id))
      void refetchCategories()
    } catch (e) {
      setError(e instanceof Error ? e.message : "删除失败")
    } finally {
      setInstalling(false)
    }
  }

  async function updateVersion(skill: MarketplaceSkill, files: PreparedSkillFile[], metadata: UploadMetadata) {
    setUploading(true)
    setError(null)
    try {
      // files 已是归一化的 skill 相对路径 + 字节（由组件从文件夹/zip 构造），controller 不再处理 webkitRelativePath
      const filePayload = files.map((f) => ({ path: f.path, content_base64: bytesToBase64(f.bytes) }))
      const updated = await apiFetch<MarketplaceSkill>(`/${skill.id}/version`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ metadata: JSON.stringify(metadata), files: filePayload }),
      })
      // 本地 patch 该 skill，保持滚动位置
      patchSkill(skill.id, updated)
      void refetchCategories()
    } catch (e) {
      setError(e instanceof Error ? e.message : "版本更新失败")
    } finally {
      setUploading(false)
    }
  }

  // 仅更新技能属性（metadata-only，不动文件）：PATCH /:skillId/attributes
  async function updateAttributes(skill: MarketplaceSkill, metadata: UpdateSkillAttributes) {
    setUploading(true)
    setError(null)
    try {
      const updated = await apiFetch<MarketplaceSkill>(`/${skill.id}/attributes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(metadata),
      })
      // 本地 patch 该 skill，保持滚动位置
      patchSkill(skill.id, updated)
      void refetchCategories()
    } catch (e) {
      setError(e instanceof Error ? e.message : "属性更新失败")
    } finally {
      setUploading(false)
    }
  }

  async function uploadSkill(files: PreparedSkillFile[], metadata: UploadMetadata) {
    setUploading(true)
    setError(null)
    try {
      // files 已是归一化的 skill 相对路径 + 字节（由组件从文件夹/zip 构造），controller 不再处理 webkitRelativePath
      const filePayload = files.map((f) => ({ path: f.path, content_base64: bytesToBase64(f.bytes) }))
      await apiFetch("/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ metadata: JSON.stringify(metadata), files: filePayload }),
      })
      // 上传后重置到第 1 页，让新技能出现在列表里
      void loadPage(true)
      void refetchCategories()
    } catch (e) {
      setError(e instanceof Error ? e.message : "上传失败")
    } finally {
      setUploading(false)
    }
  }

  // 创建自定义技能分类：POST /api/skill-market/categories → 写入 skill_category 表
  // 成功后 refetchCategories 刷新下拉，并返回新分类（供上传弹框自动选中）
  const [creatingCategory, setCreatingCategory] = createSignal(false)

  async function createCategory(input: CreateCategoryInput): Promise<SkillCategory | null> {
    setCreatingCategory(true)
    setError(null)
    try {
      const created = await apiFetch<SkillCategory>("/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      })
      // 刷新分类下拉，让新分类出现
      await refetchCategories()
      return created
    } catch (e) {
      setError(e instanceof Error ? e.message : "创建分类失败")
      return null
    } finally {
      setCreatingCategory(false)
    }
  }

  return {
    skills,
    selectedSkill,
    selectedSkillId,
    selectSkill,
    deselectSkill,
    selectedFilePath,
    selectFile,
    fileContent,
    fileLoading,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    categories,
    categoryCounts,
    hasMore,
    loadingMore,
    refreshing,
    loadMore,
    installing,
    uploading,
    showUpload,
    setShowUpload,
    error,
    installSkill,
    uninstallSkill,
    removeSkill,
    uploadSkill,
    createCategory,
    creatingCategory,
    updateVersion,
    updateAttributes,
    refetchFile: fetchFile,
  }
}

export type SkillMarketController = ReturnType<typeof createSkillMarketController>
